import { useCallback, useEffect, useRef, useState } from 'react';
import { speakReply } from '../../api/bookingAgent';

interface SpeechRecognitionInstance {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent {
  error: string;
}

// The Web Speech API is vendor-prefixed and not in the standard TS DOM lib,
// so we resolve the constructor loosely. `undefined` on unsupported browsers
// (notably Firefox) — the UI hides the mic and falls back to text.
const SpeechRecognitionImpl: (new () => SpeechRecognitionInstance) | undefined =
  typeof window !== 'undefined'
    ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    : undefined;

const RESTART_LISTEN_DELAY_MS = 200;
const IOS_IDLE_RETRY_MS = 900;

function isAppleMobile(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function getListenRestartDelayMs(): number {
  return isAppleMobile() ? 450 : RESTART_LISTEN_DELAY_MS;
}

function getVoiceErrorMessage(errorCode: string): string | null {
  if (errorCode === 'not-allowed' || errorCode === 'service-not-allowed') {
    return 'Microphone access was denied. Allow the mic in browser settings and try again.';
  }
  if (errorCode === 'audio-capture') {
    return 'Could not access the microphone. Check that no other app is using it.';
  }
  if (errorCode === 'network') {
    return 'Voice needs an internet connection. Check your signal and try again.';
  }
  if (errorCode === 'aborted' || errorCode === 'no-speech') {
    return null;
  }
  return `Voice error: ${errorCode}`;
}

async function warmUpMicrophone(): Promise<boolean> {
  if (!navigator.mediaDevices?.getUserMedia) {
    return false;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => {
      track.stop();
    });
    return true;
  } catch {
    return false;
  }
}

interface UseVoiceConversationArgs {
  // Return false when the transcript was not sent (e.g. session not ready).
  onTranscript: (text: string) => boolean | Promise<boolean>;
  // When true, stop listening (agent loading, OTP entry, deposit UI, etc.).
  paused?: boolean;
}

export interface VoiceConversation {
  supported: boolean;
  voiceMode: boolean;
  isListening: boolean;
  isSpeaking: boolean;
  isAwaitingReply: boolean;
  isGreeting?: boolean;
  voiceError: string | null;
  toggleVoiceMode: () => void;
  stopVoiceMode: () => void;
  resumeListening: () => void;
  // Pause/resume listening without leaving voice mode (used while an OTP or
  // deposit card is shown).
  setActionBlocked: (blocked: boolean) => void;
  // Play an agent reply aloud; loops back to listening when done.
  speak: (text: string, apiBase?: string) => void;
}

function getSpeechRecognitionLang(): string {
  const nav = typeof navigator !== 'undefined' ? navigator.language : '';
  const lower = nav.toLowerCase();
  if (lower.startsWith('en-au')) return 'en-AU';
  if (lower.startsWith('en-us')) return 'en-US';
  if (lower.startsWith('en-gb')) return 'en-GB';
  if (lower.startsWith('en')) return nav;
  return 'en-AU';
}

function unlockAudioPlayback(unlockedRef: { current: boolean }): void {
  if (unlockedRef.current) return;
  try {
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioContextCtor) {
      const ctx = new AudioContextCtor();
      const buffer = ctx.createBuffer(1, 1, 22050);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
      void ctx.close();
      unlockedRef.current = true;
      return;
    }
  } catch {
    // Fall through to silent HTMLAudioElement unlock.
  }
  const silent = new Audio();
  silent.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA==';
  void silent.play().then(() => {
    unlockedRef.current = true;
  }).catch(() => {});
}

/**
 * Hands-free voice loop for the booking agent:
 *   listen (STT) -> onTranscript -> [widget sends] -> speak (TTS) -> listen ...
 *
 * STT is the browser's Web Speech API (free, no backend). TTS is the backend
 * Polly endpoint via speakReply(). Claude itself is unchanged and text-only.
 */
export function useVoiceConversation({
  onTranscript,
  paused = false,
}: UseVoiceConversationArgs): VoiceConversation {
  const supported = Boolean(SpeechRecognitionImpl);
  const [voiceMode, setVoiceMode] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isAwaitingReply, setIsAwaitingReply] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  // A single, reused <audio> element. Reusing the element that first played
  // inside the mic-tap gesture keeps every later reply audible — a fresh
  // `new Audio()` per reply gets silently blocked by the browser autoplay
  // policy after the first one (the "voice only spoke once" bug).
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const restartTimerRef = useRef<number | null>(null);
  const idleRetryTimerRef = useRef<number | null>(null);
  const audioUnlockedRef = useRef(false);
  // Monotonic token so a stale audio's onended/onerror can't fight a newer play.
  const playTokenRef = useRef(0);
  // Refs mirror state for use inside long-lived recognition/audio callbacks.
  const voiceModeRef = useRef(false);
  const speakingRef = useRef(false);
  const awaitingReplyRef = useRef(false);
  const preparingSpeechRef = useRef(false);
  const pausedRef = useRef(paused);
  // Set while an OTP/deposit card is up — pauses listening without exiting voice.
  const actionBlockedRef = useRef(false);
  const onTranscriptRef = useRef(onTranscript);
  const resumeListeningRef = useRef<() => void>(() => {});
  const pendingTranscriptRef = useRef<string | null>(null);
  // iOS WebKit often omits isFinal on later turns — keep the best interim heard.
  const latestCapturedSpeechRef = useRef('');
  const transcriptDispatchedRef = useRef(false);

  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);
  useEffect(() => { voiceModeRef.current = voiceMode; }, [voiceMode]);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  const clearRestartTimer = useCallback(() => {
    if (restartTimerRef.current !== null) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
  }, []);

  const clearIdleRetryTimer = useCallback(() => {
    if (idleRetryTimerRef.current !== null) {
      window.clearTimeout(idleRetryTimerRef.current);
      idleRetryTimerRef.current = null;
    }
  }, []);

  const setAwaitingReply = useCallback((value: boolean) => {
    awaitingReplyRef.current = value;
    setIsAwaitingReply(value);
  }, []);

  const dispatchTranscript = useCallback((transcript: string) => {
    setAwaitingReply(true);
    clearRestartTimer();
    clearIdleRetryTimer();
    void Promise.resolve(onTranscriptRef.current(transcript)).then((handled) => {
      if (handled) {
        return;
      }
      if (pausedRef.current) {
        pendingTranscriptRef.current = transcript;
        return;
      }
      setAwaitingReply(false);
      resumeListeningRef.current();
    });
  }, [clearIdleRetryTimer, clearRestartTimer, setAwaitingReply]);

  useEffect(() => {
    if (paused || !voiceModeRef.current) {
      return;
    }
    const pending = pendingTranscriptRef.current;
    if (!pending) {
      return;
    }
    pendingTranscriptRef.current = null;
    dispatchTranscript(pending);
  }, [paused, dispatchTranscript]);

  const shouldListen = useCallback(() => {
    return !pausedRef.current
      && !actionBlockedRef.current
      && !speakingRef.current
      && !awaitingReplyRef.current
      && !preparingSpeechRef.current;
  }, []);

  const scheduleListenRestart = useCallback(() => {
    if (!voiceModeRef.current || !shouldListen()) {
      return;
    }
    clearRestartTimer();
    restartTimerRef.current = window.setTimeout(() => {
      restartTimerRef.current = null;
      if (voiceModeRef.current && shouldListen()) {
        safeStartListeningRef.current();
      }
    }, getListenRestartDelayMs());
  }, [clearRestartTimer, shouldListen]);

  const safeStartListeningRef = useRef<() => void>(() => {});

  const destroyRecognition = useCallback((recognition: SpeechRecognitionInstance | null) => {
    if (!recognition) {
      return;
    }
    recognition.onstart = null;
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    try {
      recognition.stop();
    } catch {
      // noop
    }
  }, []);

  const createRecognition = useCallback((): SpeechRecognitionInstance | null => {
    if (!SpeechRecognitionImpl) {
      return null;
    }
    const recognition = new SpeechRecognitionImpl();
    recognition.lang = getSpeechRecognitionLang();
    recognition.interimResults = true;
    recognition.continuous = false;

    recognition.onstart = () => {
      setIsListening(true);
      setVoiceError(null);
      transcriptDispatchedRef.current = false;
      latestCapturedSpeechRef.current = '';
      clearIdleRetryTimer();
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = '';
      let interimTranscript = '';
      for (let index = 0; index < event.results.length; index += 1) {
        const result = event.results[index] as any;
        const piece = result[0]?.transcript ?? '';
        if (result.isFinal) {
          finalTranscript += piece;
        } else {
          interimTranscript += piece;
        }
      }
      if (isAppleMobile() && interimTranscript.trim()) {
        latestCapturedSpeechRef.current = interimTranscript.trim();
      }
      const transcript = finalTranscript.trim();
      if (!transcript) {
        return;
      }
      if (transcriptDispatchedRef.current) {
        return;
      }
      transcriptDispatchedRef.current = true;
      latestCapturedSpeechRef.current = '';
      setAwaitingReply(true);
      clearRestartTimer();
      try {
        recognition.stop();
      } catch {
        // noop
      }
      dispatchTranscript(transcript);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      setIsListening(false);
      const message = getVoiceErrorMessage(event.error);
      if (message) {
        setVoiceError(message);
      }
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        voiceModeRef.current = false;
        setVoiceMode(false);
        setAwaitingReply(false);
        preparingSpeechRef.current = false;
      }
    };

    recognition.onend = () => {
      setIsListening(false);

      if (
        isAppleMobile()
        && !transcriptDispatchedRef.current
        && latestCapturedSpeechRef.current.trim()
        && !awaitingReplyRef.current
        && !speakingRef.current
        && !preparingSpeechRef.current
      ) {
        const pending = latestCapturedSpeechRef.current.trim();
        transcriptDispatchedRef.current = true;
        latestCapturedSpeechRef.current = '';
        dispatchTranscript(pending);
        return;
      }

      if (
        voiceModeRef.current
        && !speakingRef.current
        && !awaitingReplyRef.current
        && !preparingSpeechRef.current
        && !pausedRef.current
        && !actionBlockedRef.current
      ) {
        scheduleListenRestart();
      }
    };

    return recognition;
  }, [clearIdleRetryTimer, clearRestartTimer, dispatchTranscript, scheduleListenRestart, setAwaitingReply]);

  const safeStartListening = useCallback(() => {
    if (!supported) {
      return;
    }
    if (!shouldListen()) {
      return;
    }

    if (isAppleMobile()) {
      destroyRecognition(recognitionRef.current);
      recognitionRef.current = createRecognition();
    } else if (!recognitionRef.current) {
      recognitionRef.current = createRecognition();
    }

    const recognition = recognitionRef.current;
    if (!recognition) {
      return;
    }

    try {
      recognition.start();
      return;
    } catch {
      // iOS often needs stop → brief pause → start.
    }
    try {
      recognition.stop();
    } catch {
      // noop
    }
    window.setTimeout(() => {
      if (!shouldListen()) {
        return;
      }
      try {
        recognition.start();
      } catch {
        scheduleListenRestart();
      }
    }, isAppleMobile() ? 300 : 100);
  }, [createRecognition, destroyRecognition, scheduleListenRestart, shouldListen, supported]);

  useEffect(() => {
    safeStartListeningRef.current = safeStartListening;
  }, [safeStartListening]);

  const startListening = useCallback(() => {
    safeStartListening();
  }, [safeStartListening]);

  const stopCurrentAudio = useCallback(() => {
    playTokenRef.current += 1;
    const audio = audioRef.current;
    const url = audioUrlRef.current;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
    }
    if (url) {
      URL.revokeObjectURL(url);
      audioUrlRef.current = null;
    }
    speakingRef.current = false;
    setIsSpeaking(false);
  }, []);

  const resumeListening = useCallback(() => {
    setAwaitingReply(false);
    preparingSpeechRef.current = false;
    if (voiceModeRef.current && shouldListen()) {
      safeStartListening();
    }
  }, [safeStartListening, setAwaitingReply, shouldListen]);

  useEffect(() => {
    resumeListeningRef.current = resumeListening;
  }, [resumeListening]);

  useEffect(() => {
    if (!voiceModeRef.current) return;
    if (paused) {
      clearRestartTimer();
      clearIdleRetryTimer();
      setIsListening(false);
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
      return;
    }
    if (!speakingRef.current && !awaitingReplyRef.current && !preparingSpeechRef.current) {
      startListening();
    }
  }, [paused, clearIdleRetryTimer, clearRestartTimer, startListening]);

  // iOS watchdog: if voice mode is active but STT never reaches onstart ("Getting ready…"),
  // retry starting recognition after a short idle period.
  useEffect(() => {
    if (!isAppleMobile() || !voiceMode || paused) {
      clearIdleRetryTimer();
      return;
    }
    if (isListening || isSpeaking || isAwaitingReply) {
      clearIdleRetryTimer();
      return;
    }
    clearIdleRetryTimer();
    idleRetryTimerRef.current = window.setTimeout(() => {
      idleRetryTimerRef.current = null;
      if (voiceModeRef.current && shouldListen()) {
        safeStartListeningRef.current();
      }
    }, IOS_IDLE_RETRY_MS);
    return () => {
      clearIdleRetryTimer();
    };
  }, [
    voiceMode,
    paused,
    isListening,
    isSpeaking,
    isAwaitingReply,
    clearIdleRetryTimer,
    shouldListen,
  ]);

  useEffect(() => {
    if (!supported) return;
    recognitionRef.current = createRecognition();
    return () => {
      clearRestartTimer();
      clearIdleRetryTimer();
      destroyRecognition(recognitionRef.current);
      recognitionRef.current = null;
    };
  }, [supported, createRecognition, clearRestartTimer, clearIdleRetryTimer, destroyRecognition]);

  const speak = useCallback(async (text: string, apiBase?: string) => {
    if (!voiceModeRef.current) return;

    preparingSpeechRef.current = true;
    stopCurrentAudio();
    clearRestartTimer();
    clearIdleRetryTimer();
    try {
      recognitionRef.current?.stop();
    } catch {
      // noop
    }

    const url = await speakReply(text, apiBase);
    preparingSpeechRef.current = false;

    if (!voiceModeRef.current) {
      if (url) URL.revokeObjectURL(url);
      setAwaitingReply(false);
      return;
    }
    if (!url) {
      setAwaitingReply(false);
      resumeListening();
      return;
    }

    let audio = audioRef.current;
    if (!audio) {
      audio = new Audio();
      audioRef.current = audio;
    }
    audioUrlRef.current = url;
    audio.src = url;
    setAwaitingReply(false);
    speakingRef.current = true;
    setIsSpeaking(true);

    playTokenRef.current += 1;
    const token = playTokenRef.current;
    const finish = () => {
      if (token !== playTokenRef.current) return;
      stopCurrentAudio();
      if (voiceModeRef.current && !pausedRef.current && !actionBlockedRef.current) {
        safeStartListening();
      }
    };
    audio.onended = finish;
    audio.onerror = finish;
    try {
      await audio.play();
    } catch {
      finish();
    }
  }, [clearIdleRetryTimer, clearRestartTimer, resumeListening, safeStartListening, setAwaitingReply, stopCurrentAudio]);

  const stopVoiceMode = useCallback(() => {
    voiceModeRef.current = false;
    setVoiceMode(false);
    setAwaitingReply(false);
    preparingSpeechRef.current = false;
    pendingTranscriptRef.current = null;
    latestCapturedSpeechRef.current = '';
    transcriptDispatchedRef.current = false;
    actionBlockedRef.current = false;
    clearRestartTimer();
    clearIdleRetryTimer();
    destroyRecognition(recognitionRef.current);
    recognitionRef.current = null;
    stopCurrentAudio();
    setIsListening(false);
    setVoiceError(null);
  }, [clearIdleRetryTimer, clearRestartTimer, destroyRecognition, setAwaitingReply, stopCurrentAudio]);

  const toggleVoiceMode = useCallback(() => {
    if (voiceModeRef.current) {
      stopVoiceMode();
      return;
    }
    unlockAudioPlayback(audioUnlockedRef);
    voiceModeRef.current = true;
    setVoiceMode(true);
    setVoiceError(null);
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    if (!recognitionRef.current) {
      recognitionRef.current = createRecognition();
    }
    // Start STT synchronously while we still have the tap gesture (iOS requirement).
    startListening();
    // getUserMedia after start() steals the mic from webkitSpeechRecognition on iOS.
    if (!isAppleMobile()) {
      void warmUpMicrophone().then((warmed) => {
        if (!warmed && voiceModeRef.current) {
          setVoiceError('Allow microphone access when prompted, then tap the mic again.');
        }
      });
    }
  }, [createRecognition, startListening, stopVoiceMode]);

  const setActionBlocked = useCallback((blocked: boolean) => {
    actionBlockedRef.current = blocked;
    if (blocked) {
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
    }
  }, []);

  return {
    supported,
    voiceMode,
    isListening,
    isSpeaking,
    isAwaitingReply,
    isGreeting: false,
    voiceError,
    toggleVoiceMode,
    stopVoiceMode,
    resumeListening,
    setActionBlocked,
    speak,
  };
}
